import { useEffect, useState } from 'react';
import { fetchTags } from '../../services/shipStationAPI';
import { getTagNameFromEmail, tagDictionaryMaker } from '../../contexts/TagStorage';
import { getApiErrorMessage } from '../../services/errorUtils';

export interface WorkerUserTagState {
  readonly loading: boolean;
  readonly error: string | null;
  readonly userTag: string | number | null;
}

export function useWorkerUserTag(email: string | undefined): WorkerUserTagState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userTag, setUserTag] = useState<string | number | null>(null);

  useEffect(() => {
    if (!email) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        const tagsResponse = await fetchTags({ forceRefresh: true });
        const tagDictionary = tagDictionaryMaker(tagsResponse);
        const tagName = getTagNameFromEmail(email, tagDictionary);
        const resolved = tagName ? tagDictionary[tagName] : undefined;
        if (!cancelled) {
          if (resolved == null) {
            setError(`No tag found for user: ${tagName ?? email}`);
            setUserTag(null);
          } else {
            setUserTag(resolved);
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'Failed to load user tag'));
          setUserTag(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [email]);

  return { loading, error, userTag };
}
